import { defaultLabels, type BusinessLabels } from "@teamna/shared";
import { useActiveBusiness, useOwnerBusiness } from "./use-owner.ts";

export type LabelConcept = "business" | "staff" | "member" | "participant" | "group";

function pick(labels: BusinessLabels, concept: LabelConcept, plural: boolean): string {
  const key = `${concept}${plural ? "Plural" : "Singular"}` as keyof BusinessLabels;
  return labels[key];
}

function capitalize(word: string): string {
  return word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1);
}

/**
 * The five words the active business picked for itself (business, staff, member, participant,
 * group), each singular and plural. Every owner-side screen string that names a person, a group,
 * or the business should read it from here rather than hardcoding an English or a sport word.
 *
 * Every team role reads the business (the front desk and staff get it without the owner's fields).
 * Until it arrives, and if the read fails, the words come from the business's row of
 * `/me/businesses`, which every role gets; before that list loads, the neutral "other" defaults. So
 * a screen never shows a word from a different business or sport.
 */
export function useLabels(): {
  labels: BusinessLabels;
  label: (concept: LabelConcept, opts?: { plural?: boolean; capital?: boolean }) => string;
} {
  const { business } = useActiveBusiness();
  const query = useOwnerBusiness(business?.businessId ?? null);
  const labels = query.data?.labels ?? business?.labels ?? defaultLabels(business?.sport ?? "other");

  function label(concept: LabelConcept, opts?: { plural?: boolean; capital?: boolean }): string {
    const word = pick(labels, concept, opts?.plural ?? false);
    return opts?.capital ? capitalize(word) : word;
  }

  return { labels, label };
}
